import {
  HttpContextToken,
  HttpEvent,
  HttpEventType,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { finalize, map, mergeMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { SpinnerService } from '../services/spinner.service';

/**
 * Opt out of the global loading overlay + artificial latency for one request:
 * `http.get(url, { context: new HttpContext().set(SKIP_LOADING, true) })`
 */
export const SKIP_LOADING = new HttpContextToken<boolean>(() => false);

/**
 * Opt out of artificial response delay only (spinner still runs):
 * `http.get(url, { context: new HttpContext().set(SKIP_HTTP_DELAY, true) })`
 */
export const SKIP_HTTP_DELAY = new HttpContextToken<boolean>(() => false);

/**
 * Global loading + latency interceptor.
 *
 * - Shows SpinnerService for the duration of each API call
 * - Delays HttpResponse delivery by `environment.httpResponseDelayMs` (dev)
 *   so loading states are visible and easy to test
 */
export const loadingInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SKIP_LOADING) || req.url.includes('/assets/')) {
    return next(req);
  }

  const spinner = inject(SpinnerService);
  const delayMs = resolveDelayMs(req.context.get(SKIP_HTTP_DELAY));

  spinner.show();

  return next(req).pipe(
    mergeMap((event: HttpEvent<unknown>) => delayResponseEvent(event, delayMs)),
    finalize(() => spinner.hide()),
  );
};

function resolveDelayMs(skipDelay: boolean): number {
  if (skipDelay) return 0;
  const configured = Number(environment.httpResponseDelayMs ?? 0);
  return Number.isFinite(configured) && configured > 0 ? configured : 0;
}

/** Delay only the final response event so progress/sent events stay immediate. */
function delayResponseEvent(
  event: HttpEvent<unknown>,
  delayMs: number,
): Observable<HttpEvent<unknown>> {
  if (delayMs <= 0 || event.type !== HttpEventType.Response) {
    return of(event);
  }
  return timer(delayMs).pipe(map(() => event));
}
